/* /app/frontend/src/components/dealroom/MarkFallenThroughModal.jsx
 *
 * iter-alignment · P0 · Deal Room · Fallen Through (2026-02)
 * ----------------------------------------------------------
 * Small portalled modal invoked from the Deal Room overflow menu.
 * Captures the operator's fall-through metadata (date + reason
 * + optional note) and POSTs to the canonical `mark-fallen-
 * through` endpoint.  On success the deal room reload runs so
 * every downstream SSOT surface (journey rail, NBA, tasks,
 * chases, banner, forecasts) picks up the terminal state on the
 * next tick.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle, XCircle } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { useChainStore } from "../../data/chainStore";


/* Default reason list — swap freely from the Deal Room orchestrator
 * if the operator brief lands with a bespoke taxonomy. */
export const DEFAULT_FALL_THROUGH_REASONS = [
  "Buyer withdrew",
  "Seller withdrew",
  "Chain collapsed",
  "Mortgage declined",
  "Survey issues",
  "Legal issues",
  "Gazumped",
  "Other",
];


/* iter-alignment · P0 · Mark Fallen Through — Address SSOT + Date
 * Reliability Fix (2026-03-01)
 * ---------------------------------------------------------------
 * Two pilot-blocking issues fixed here without any modal redesign:
 *
 *  1. Property subtitle now reads from the canonical Property
 *     SSOT (`chainStore.findProperty(deal.propertyId).address`)
 *     with a fall-back to `deal.property_address || deal.name`
 *     for un-hydrated / detached rows.  This is the same
 *     resolution the Deal Room hero already runs, so the two
 *     surfaces always render the same address string.
 *
 *  2. Fall-through date input replaced with a native
 *     `<input type="date">`, bound directly to the ISO
 *     `YYYY-MM-DD` state.  Native date inputs treat the value
 *     attribute as ISO, so we never pass `DD/MM/YYYY` to them.
 *     Typing / calendar picking / delete / backspace / retype
 *     all work exactly as the browser handles it — no controlled
 *     `useEffect` resets, no character-level validation.  We
 *     only sanity-check the date on submit.
 */
export default function MarkFallenThroughModal({
  deal,
  reasons = DEFAULT_FALL_THROUGH_REASONS,
  onClose,
  onCompleted,
}) {
  const dealId = deal?.id;
  const propertyId = deal?.propertyId || deal?.property_id;
  const { findProperty } = useChainStore();
  const property = propertyId && findProperty ? findProperty(propertyId) : null;
  const canonicalAddress =
    property?.address
    || deal?.property_address
    || deal?.name
    || "This transaction";

  const _todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [fallenAt, setFallenAt] = useState(_todayIso);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [dateError, setDateError] = useState(null);

  // Body scroll lock.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape dismissal.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !busy) onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [busy, onClose]);

  const canSave = useMemo(
    () => !!(dealId && /^\d{4}-\d{2}-\d{2}$/.test(fallenAt || "") && reason && reason.trim().length > 0),
    [dealId, fallenAt, reason],
  );

  const handleConfirm = useCallback(async () => {
    if (!canSave) return;
    // Blur-/submit-time validation — never character-by-character.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fallenAt || "")) {
      setDateError("Enter a valid fall-through date.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/deals/${dealId}/mark-fallen-through`, {
        fallen_at: fallenAt,
        reason:    reason.trim(),
        note:      note.trim() || null,
      });
      await Promise.resolve(onCompleted?.());
      toast.success("Deal marked as fallen through", { duration: 1800 });
      onClose?.();
    } catch (e) {
      toast.error(apiError(e, "Could not mark as fallen through"));
    } finally {
      setBusy(false);
    }
  }, [canSave, dealId, fallenAt, reason, note, onCompleted, onClose]);

  if (!deal) return null;

  const node = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 p-4"
      data-testid="mark-fallen-through-overlay"
      onClick={busy ? undefined : onClose}
      style={{ overscrollBehavior: "contain" }}
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Mark deal as fallen through"
        data-testid="mark-fallen-through-modal"
      >
        {/* Sticky header */}
        <header className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-white">
          <div className="min-w-0 flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-rose-100 text-rose-700 inline-flex items-center justify-center shrink-0">
              <XCircle size={14} strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <h3 className="text-[14px] font-bold text-slate-900 leading-tight">Mark as fallen through</h3>
              <p
                className="mt-0.5 text-[11.5px] text-slate-500 truncate"
                data-testid="mark-fallen-through-property-subtitle"
                title={canonicalAddress}
              >
                {canonicalAddress}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mark-fallen-through-close"
            aria-label="Close"
          >
            <X size={16} strokeWidth={2.4} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          <div className="rounded-lg bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5 flex items-start gap-2" data-testid="mark-fallen-through-warning">
            <AlertTriangle size={13} className="text-rose-600 mt-0.5 shrink-0" strokeWidth={2.4} />
            <p className="text-[12px] text-rose-800 leading-relaxed">
              This suspends NBA, tasks, chases and automation, and excludes the deal from live pipeline, forecasts and revenue. History is preserved and the transaction can be reopened at any time.
            </p>
          </div>

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Fall-through date</span>
            <input
              type="date"
              value={fallenAt}
              onChange={(e) => { setFallenAt(e.target.value); if (dateError) setDateError(null); }}
              onBlur={() => {
                if (fallenAt && !/^\d{4}-\d{2}-\d{2}$/.test(fallenAt)) {
                  setDateError("Enter a valid fall-through date.");
                } else {
                  setDateError(null);
                }
              }}
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-300"
              data-testid="mark-fallen-through-date"
              aria-label="Fall-through date"
            />
            {dateError ? (
              <span className="mt-1 block text-[10.5px] text-rose-600" data-testid="mark-fallen-through-date-error">
                {dateError}
              </span>
            ) : (
              <span className="mt-1 block text-[10.5px] text-slate-400">Defaults to today — edit if the deal collapsed earlier.</span>
            )}
          </label>

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Reason</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-300"
              data-testid="mark-fallen-through-reason"
            >
              <option value="">Select a reason…</option>
              {reasons.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Note (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Anything the team should see later — chain link, mortgage lender, expected timing…"
              className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-rose-300 resize-none"
              data-testid="mark-fallen-through-note"
            />
          </label>
        </div>

        {/* Footer */}
        <footer className="shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
            data-testid="mark-fallen-through-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSave || busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="mark-fallen-through-confirm"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} strokeWidth={2.4} />}
            {busy ? "Marking…" : "Mark as fallen through"}
          </button>
        </footer>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
